import { useMemo, useState } from 'react';
import {
  checkInProgressSeries,
  checkInProgressTrend,
  daysBetweenDateKeys,
  type CheckInProgressPoint,
} from '../lib/stats';
import type { WpmCheckIn } from '../types';

type RangeDays = 90 | 180 | 365;

interface CheckInProgressChartProps {
  checkIns: WpmCheckIn[] | undefined;
}

export default function CheckInProgressChart({
  checkIns,
}: CheckInProgressChartProps) {
  const [range, setRange] = useState<RangeDays>(180);

  const series = useMemo(
    () => checkInProgressSeries(checkIns, range),
    [checkIns, range],
  );
  const { rangeStart, rangeEnd, points } = series;
  const trend = useMemo(() => checkInProgressTrend(points), [points]);

  if (!checkIns?.length) {
    return (
      <p className="text-sm text-muted">
        Ingen hastighedstests endnu. Tag din første test for at se udvikling
        over tid.
      </p>
    );
  }

  if (points.length === 0) {
    return (
      <div>
        <RangeToggle range={range} onChange={setRange} />
        <p className="mt-3 text-sm text-muted">
          Ingen tests i de seneste {range} dage. Vælg en længere periode, eller
          tag en ny test.
        </p>
      </div>
    );
  }

  return (
    <div>
      <RangeToggle range={range} onChange={setRange} />
      <div className="mt-3 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-xs text-muted">
            {points.length} {points.length === 1 ? 'dag' : 'dage'} med test ·
            bedste WPM pr. dag
          </p>
          {trend.delta !== null && (
            <p
              className={`font-mono text-sm font-medium ${
                trend.delta >= 0
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-rose-700 dark:text-rose-400'
              }`}
            >
              {trend.delta >= 0 ? '+' : ''}
              {trend.delta.toFixed(0)} wpm over perioden
            </p>
          )}
        </div>
      <CheckInLineChart
        points={points}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
      />
    </div>
  );
}

function RangeToggle({
  range,
  onChange,
}: {
  range: RangeDays;
  onChange: (v: RangeDays) => void;
}) {
  return (
    <div className="inline-flex rounded-lg bg-zinc-100 p-0.5 ring-1 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5">
      {(
        [
          [90, '90 dage'],
          [180, '180 dage'],
          [365, '1 år'],
        ] as const
      ).map(([days, label]) => (
        <button
          key={days}
          type="button"
          onClick={() => onChange(days)}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
            range === days
              ? 'bg-white text-strong shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-white/10'
              : 'text-muted hover:text-strong'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function CheckInLineChart({
  points,
  rangeStart,
  rangeEnd,
}: {
  points: CheckInProgressPoint[];
  rangeStart: string;
  rangeEnd: string;
}) {
  const width = 640;
  const height = 160;
  const pad = { top: 12, right: 12, bottom: 28, left: 40 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const spanDays = Math.max(daysBetweenDateKeys(rangeStart, rangeEnd), 1);

  const xForDate = (date: string) => {
    const offset = daysBetweenDateKeys(rangeStart, date);
    return pad.left + (offset / spanDays) * innerW;
  };

  const wpms = points.map((p) => p.wpm);
  const minVal = Math.min(...wpms);
  const maxVal = Math.max(...wpms);
  const yMin = Math.max(0, Math.floor(minVal - 5));
  const yMax = Math.ceil(maxVal + 5);
  const ySpan = yMax - yMin || 1;

  const yForValue = (v: number) =>
    pad.top + innerH - ((v - yMin) / ySpan) * innerH;

  const linePoints = points.map((p) => ({
    x: xForDate(p.date),
    y: yForValue(p.wpm),
  }));

  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
    yMin + (ySpan * i) / yTicks,
  );

  const labelDates = calendarLabelDates(rangeStart, rangeEnd);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="mt-3 h-auto w-full text-faint"
      role="img"
      aria-label="WPM over tid fra hastighedstests"
    >
      {tickValues.map((v) => {
        const y = yForValue(v);
        return (
          <g key={v}>
            <line
              x1={pad.left}
              y1={y}
              x2={width - pad.right}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.12}
            />
            <text
              x={pad.left - 6}
              y={y + 4}
              textAnchor="end"
              className="fill-current text-[10px]"
            >
              {Math.round(v)}
            </text>
          </g>
        );
      })}

      {linePoints.length > 1 && (
        <polyline
          fill="none"
          stroke="currentColor"
          className="text-indigo-500 dark:text-indigo-400"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={linePoints.map((pt) => `${pt.x},${pt.y}`).join(' ')}
        />
      )}

      {points.map((p) => {
        const cx = xForDate(p.date);
        const cy = yForValue(p.wpm);
        return (
          <circle
            key={p.date}
            cx={cx}
            cy={cy}
            r={4}
            className="fill-indigo-500 dark:fill-indigo-400"
          >
            <title>
              {formatChartDate(p.date)}: {p.wpm.toFixed(0)} wpm ·{' '}
              {p.accuracy.toFixed(0)}% · {p.solidKeyCount} taster
            </title>
          </circle>
        );
      })}

      {labelDates.map((date) => (
        <text
          key={date}
          x={xForDate(date)}
          y={height - 6}
          textAnchor="middle"
          className="fill-current text-[10px]"
        >
          {formatChartDate(date, true)}
        </text>
      ))}
    </svg>
  );
}

function calendarLabelDates(rangeStart: string, rangeEnd: string): string[] {
  const span = daysBetweenDateKeys(rangeStart, rangeEnd);
  const count = span <= 14 ? Math.min(span + 1, 7) : span <= 90 ? 5 : 6;
  if (span <= 0) return [rangeStart];
  if (count <= 1) return [rangeStart];

  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const offset = Math.round((i / (count - 1)) * span);
    const d = new Date(`${rangeStart}T12:00:00`);
    d.setDate(d.getDate() + offset);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    out.push(`${yyyy}-${mm}-${dd}`);
  }
  return [...new Set(out)];
}

function formatChartDate(date: string, short = false): string {
  try {
    const d = new Date(`${date}T12:00:00`);
    if (short) {
      return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
    }
    return d.toLocaleDateString('da-DK', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return date;
  }
}
